import {useCallback, useEffect, useRef} from 'react'
import FilterDropdown, {
  FILTER_SELECT_ALL,
  SelectAllDropdownItem,
} from './FilterDropdown.js'
import {simpleFilter} from '../tokenizeOptions.js'
import {
  getSupplierName,
  supplierOptionsSelector,
  supplierOptionTokensSelector,
} from '../../data/suppliers.js'
import {updateForm, useAutoForm, useSelector} from '../../store.js'
import {useZropdown, useZropdownElement} from './Zropdown.js'
import {currentDropdownSelector} from '../../redux/selectors/ui/index.js'
import DropdownItem from './Dropdown/DropdownItem.js'
import className from '../className.js'
import Checkbox from './Checkbox.js'

function SupplierDropdownItem({option, isFocused, onSelect}) {
  const supplier = option.entity
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <DropdownItem>
      <div
        className={`wrap--dropdown-item flex--justify-nowrap label--selectable label--selectable-pt-pb-0 margin-bottom-0 ${
          isFocused ? 'list__link--dropdown-arrow-focus' : ''
        }`}
      >
        <button
          className="btn--link list__link--dropdown list__link--supplier"
          type="button"
          onClick={(event) => onSelect(option, event)}
        >
          <span className="btn__text">{getSupplierName(supplier)}</span>
        </button>
      </div>
    </DropdownItem>
  )
}

function SupplierCheckboxItem({
  option,
  selected,
  disabled,
  isFocused,
  onSelect,
}) {
  const supplier = option.entity
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <li ref={ref} className="list__item list__item--dropdown">
      <span
        className={className`label--selectable label--selectable-pt-pb-0 flex--justify-nowrap label--selectable-left-indent margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
          disabled,
        }}`}
      >
        <label
          className="flex--justify-nowrap flex-grow flex--jc-left mw-unset margin-bottom-0"
          htmlFor={supplier.id}
          data-dropdown-prevent-close
        >
          <span className="margin-right-5">
            <Checkbox
              mode="fancy"
              className="fs-00 v-align-middle"
              id={supplier.id}
              checked={selected}
              onClick={(value, event) => {
                if (!disabled) {
                  onSelect(option, event)
                }
              }}
            />
          </span>
          <span className="label--selectable__text">{option.display}</span>
        </label>
      </span>
    </li>
  )
}

export default function SupplierFilter({
  dropdown = 'supplier_filter_dropdown',
  label = 'Select Supplier',
  value,
  hasErrors,
  onSelect,
  onChange,
  excludeSupplierIDs,
  includeSelectAll = false,
}) {
  const isMultiSelect = Array.isArray(value)

  const form = useAutoForm(dropdown, {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    hasChanged: false,
    serverError: null,
  })

  const supplierOptions = useSelector(supplierOptionsSelector)
  const supplierOptionTokens = useSelector(supplierOptionTokensSelector)

  const onFilter = useCallback(() => {
    simpleFilter({
      formName: dropdown,
      options: supplierOptions,
      optionTokens: supplierOptionTokens,
      excludeIDs: excludeSupplierIDs,
      includeSelectAll,
    })
  }, [supplierOptions, excludeSupplierIDs])

  useEffect(onFilter, [supplierOptions, excludeSupplierIDs])

  const buttonRef = useRef()

  const localOnChange = useCallback(() => {
    updateForm(dropdown, {hasChanged: true})
  }, [])

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onChange: onFilter,
      onSelect: (...args) => {
        onSelect(...args)

        localOnChange()
      },
    })

  const currentDropdown = useSelector(currentDropdownSelector)

  useEffect(() => {
    if (!currentDropdown && form.hasChanged) {
      updateForm(dropdown, {hasChanged: false})

      if (onChange) {
        onChange()
      }
    }
  }, [currentDropdown])

  return (
    <div
      className={className`wrap-outer--tag-dropdown-with-filter margin-right-20 ${{
        open: currentDropdown === dropdown,
      }}`}
      data-dropdown={dropdown}
    >
      <button
        ref={buttonRef}
        className="btn btn--dropdown btn--filter"
        type="button"
      >
        <strong
          className={className`btn__text ${{'error-message': hasErrors}}`}
        >
          {label}
        </strong>
      </button>
      <FilterDropdown
        dropdown={dropdown}
        form={form}
        onChangeInternal={onChangeInternal}
        onConfirm={onConfirm}
        onUp={onUp}
        onDown={onDown}
        placeholder="Search Suppliers"
        includeSearchInput
        parentRef={buttonRef}
      >
        <>
          {form.results.map((option, index) =>
            option.type === FILTER_SELECT_ALL ? (
              <SelectAllDropdownItem
                key={FILTER_SELECT_ALL}
                option={option}
                selectedList={value}
                isFocused={index === form.selectedIndex}
                onSelect={onSelectInternal}
              />
            ) : isMultiSelect ? (
              <SupplierCheckboxItem
                key={option.entity.id}
                option={option}
                onSelect={onSelectInternal}
                selected={value.includes(option.entity.id)}
                isFocused={index === form.selectedIndex}
              />
            ) : (
              <SupplierDropdownItem
                key={option.entity.id}
                option={option}
                onSelect={onSelectInternal}
                selected={value.includes(option.entity.id)}
                isFocused={index === form.selectedIndex}
              />
            ),
          )}
          {form.hasFoundNothing && (
            <li className="list__item--dropdown list__item--dropdown-empty align-center">
              No suppliers match your search.
            </li>
          )}
        </>
      </FilterDropdown>
    </div>
  )
}
